import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useBookVisit } from '@/context/BookVisitContext';
import { useLanguage } from '@/context/LanguageContext';
import { useSiteData, resolveVisibility, findCategoryForProperty } from '@/context/SiteDataContext';
import { MediaGallery } from '@/components/MediaGallery';
import { ShareButton } from '@/components/ShareButton';
import { BrochureButton } from '@/components/BrochureButton';
import { PropertyCard } from '@/components/PropertyCard';
import { resolveMediaUrl, formatINR } from '@/lib/api';
import { toast } from 'sonner';
import {
  MapPin, Maximize2, Home, Building2, ArrowLeft, CheckCircle2, ExternalLink,
  Phone, MessageCircle, Calendar, Clock, Shield, Loader2, Compass, Layers, Bed, Bath, Car, Banknote, Trees,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AMENITY_GROUPS = [
  {
    title: { en: 'Plot Infrastructure', hi: 'प्लॉट सुविधाएँ' },
    items: {
      roads_internal: { en: 'Internal Roads', hi: 'आंतरिक सड़कें' },
      street_lights: { en: 'Street Lights', hi: 'स्ट्रीट लाइट' },
      drainage: { en: 'Drainage System', hi: 'ड्रेनेज सिस्टम' },
      sewerage: { en: 'Sewerage', hi: 'सीवरेज' },
      electricity: { en: 'Electricity', hi: 'बिजली' },
      water_supply: { en: 'Water Supply', hi: 'पानी की आपूर्ति' },
      compound_wall: { en: 'Compound Wall', hi: 'चारदीवारी' },
      parking: { en: 'Parking', hi: 'पार्किंग' },
      water_storage: { en: 'Water Storage', hi: 'पानी का स्टोरेज' },
    },
  },
  {
    title: { en: 'Society Amenities', hi: 'सोसाइटी सुविधाएँ' },
    items: {
      gated_campus: { en: 'Gated Campus', hi: 'गेटेड कैंपस' },
      security: { en: '24/7 Security', hi: '24/7 सुरक्षा' },
      cctv: { en: 'CCTV', hi: 'CCTV' },
      clubhouse: { en: 'Clubhouse', hi: 'क्लबहाउस' },
      gym: { en: 'Gym', hi: 'जिम' },
      swimming_pool: { en: 'Swimming Pool', hi: 'स्विमिंग पूल' },
      kids_play_area: { en: 'Kids Play Area', hi: 'बच्चों का खेल क्षेत्र' },
      garden: { en: 'Garden', hi: 'गार्डन' },
      jogging_track: { en: 'Jogging Track', hi: 'जॉगिंग ट्रैक' },
      indoor_games: { en: 'Indoor Games', hi: 'इनडोर गेम्स' },
      community_hall: { en: 'Community Hall', hi: 'कम्युनिटी हॉल' },
      intercom: { en: 'Intercom', hi: 'इंटरकॉम' },
      park_play_area: { en: 'Park / Play Area', hi: 'पार्क / खेल क्षेत्र' },
    },
  },
  {
    title: { en: 'Nearby Conveniences', hi: 'आस-पास की सुविधाएँ' },
    items: {
      nearby_market: { en: 'Market', hi: 'बाज़ार' },
      nearby_school: { en: 'School', hi: 'स्कूल' },
      nearby_hospital: { en: 'Hospital', hi: 'अस्पताल' },
      nearby_bus_stop: { en: 'Bus Stop', hi: 'बस स्टॉप' },
      loan_assistance: { en: 'Loan Assistance', hi: 'लोन सहायता' },
    },
  },
  {
    title: { en: 'Home Features', hi: 'घर की विशेषताएं' },
    items: {
      modular_kitchen: { en: 'Modular Kitchen', hi: 'मॉड्यूलर किचन' },
      wardrobes: { en: 'Wardrobes', hi: 'वार्डरोब' },
      false_ceiling: { en: 'False Ceiling', hi: 'फॉल्स सीलिंग' },
      geyser: { en: 'Geyser', hi: 'गीज़र' },
      fans_lights: { en: 'Fans & Lights', hi: 'पंखे और लाइट्स' },
      lift_available: { en: 'Lift', hi: 'लिफ्ट' },
      pantry: { en: 'Pantry', hi: 'पैंट्री' },
      fire_safety: { en: 'Fire Safety', hi: 'अग्नि सुरक्षा' },
      goods_lift: { en: 'Goods Lift', hi: 'गुड्स लिफ्ट' },
      loading_unloading_area: { en: 'Loading/Unloading Area', hi: 'लोडिंग/अनलोडिंग क्षेत्र' },
    },
  },
];

const getMapEmbedUrl = (mapLink, lat, lng) => {
  if (mapLink && mapLink.includes('google.com/maps/embed')) return mapLink;
  if (lat && lng) return `https://maps.google.com/maps?q=${lat},${lng}&hl=en&z=14&output=embed`;
  return null;
};

export default function PropertyDetailPage() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', mobile: '', email: '', preferred_date: '', preferred_time: '', message: '' });
  const { openModal } = useBookVisit();
  const { t, lang, pickLocale } = useLanguage();
  const { categories, siteSettings } = useSiteData();

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/properties/${id}`).then(({ data }) => {
      setProperty(data);
      // Fetch related (same category, exclude self)
      return axios.get(`${API}/properties?category=${encodeURIComponent(data.category)}`)
        .then(({ data: list }) => setRelated((list || []).filter((p) => p.id !== data.id).slice(0, 4)));
    }).catch((err) => {
      console.error(err);
    }).finally(() => setLoading(false));
  }, [id]);

  const visibility = useMemo(() => resolveVisibility(property, categories), [property, categories]);
  const ll = (en, hi) => (lang === 'hi' && hi ? hi : en);
  const dispTitle = property ? ll(property.title, property.title_hi) : '';
  const dispDesc = property ? ll(property.short_description, property.short_description_hi) : '';
  const dispAddr = property ? ll(property.full_address, property.full_address_hi) : '';

  if (loading) {
    return <div className="pt-20 min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-orange" /></div>;
  }

  if (!property) {
    return (
      <div className="pt-20 min-h-screen flex flex-col items-center justify-center">
        <h1 className="font-heading text-2xl font-bold text-gray-900 mb-4">Property Not Found</h1>
        <Link to="/projects"><Button variant="outline"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button></Link>
      </div>
    );
  }

  const area = property.plot_area_sqft || property.house_area_sqft || property.builtup_area_sqft || property.carpet_area_sqft;
  const totalPrice = property.total_price || (property.rate_per_sqft && area ? property.rate_per_sqft * area : null);

  const showPrice = visibility.show_total_price && Boolean(totalPrice);
  const showArea = visibility.show_area && Boolean(area);
  const showRate = visibility.show_rate_per_sqft && Boolean(property.rate_per_sqft);
  const showGov = visibility.show_gov_rate && Boolean(property.gov_rate);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) return toast.error(t('form.required', 'Name and mobile required'));
    setSubmitting(true);
    try {
      const payload = { ...form, property_id: property.id, property_title: property.title, source: 'property-detail' };
      // Omit empty email — backend EmailStr rejects ''
      if (!payload.email) delete payload.email;
      // Omit empty optional date/time/message to keep payload clean
      if (!payload.preferred_date) delete payload.preferred_date;
      if (!payload.preferred_time) delete payload.preferred_time;
      if (!payload.message) delete payload.message;
      await axios.post(`${API}/leads`, payload);
      toast.success(t('form.submitted', 'Thank you! We will contact you soon.'));
      setForm({ name: '', mobile: '', email: '', preferred_date: '', preferred_time: '', message: '' });
    } catch {
      toast.error('Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const ogTitle = property.share_preview_title || property.meta_title || `${property.title} | Shree Bajrang Developers`;
  const ogDescription = property.share_preview_description || property.meta_description || property.short_description || `${property.category} in ${property.location_label}`;
  const ogImage = resolveMediaUrl(property.share_preview_image || property.main_image || (property.gallery_images || [])[0] || siteSettings?.default_share_image);
  const mapEmbed = getMapEmbedUrl(property.google_map_link, property.latitude, property.longitude);
  const phone = property.contact_phone || siteSettings?.contact_phone || '+91 7581061649';
  const wa = (siteSettings?.contact_whatsapp || '919131709809').replace(/\D/g, '');

  // JSON-LD schema for SEO
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": property.title,
    "description": ogDescription,
    "image": ogImage,
    "sku": property.code,
    "brand": { "@type": "Brand", "name": "Shree Bajrang Developers" },
    ...(showPrice && totalPrice ? {
      "offers": { "@type": "Offer", "priceCurrency": "INR", "price": totalPrice, "availability": property.status === 'Available' ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
    } : {}),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": typeof window !== 'undefined' ? window.location.origin : '' },
      { "@type": "ListItem", "position": 2, "name": property.category, "item": `${typeof window !== 'undefined' ? window.location.origin : ''}/category/${(findCategoryForProperty(property, categories) || {}).slug || property.category.toLowerCase()}` },
      { "@type": "ListItem", "position": 3, "name": property.title },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDescription} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="pt-16 lg:pt-20" data-testid="property-detail-page">
        {/* Breadcrumb */}
        <div className="bg-gray-50 border-b">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 py-3 text-sm text-gray-600 flex items-center gap-2 overflow-x-auto whitespace-nowrap" data-testid="breadcrumb">
            <Link to="/" className="hover:text-brand-orange">Home</Link>
            <span>/</span>
            <Link to={`/category/${(findCategoryForProperty(property, categories) || {}).slug || property.category.toLowerCase()}`} className="hover:text-brand-orange">{property.category}</Link>
            <span>/</span>
            <span className="text-gray-900 font-medium truncate">{dispTitle}</span>
          </div>
        </div>

        {/* Top section: Gallery + Key info */}
        <section className="py-6 md:py-10 bg-white">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-5 gap-6 md:gap-8">
            <div className="lg:col-span-3">
              <MediaGallery
                images={[property.main_image, ...(property.gallery_images || [])].filter(Boolean)}
                videos={property.videos || []}
                videoUrls={[property.youtube_url, ...(property.video_urls || [])].filter(Boolean)}
              />
            </div>

            <div className="lg:col-span-2 space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-brand-orange/10 text-brand-orange border border-brand-orange/20">{property.category}</Badge>
                <Badge variant="outline">{property.status}</Badge>
                {property.featured && <Badge className="bg-amber-400 text-amber-950">Featured</Badge>}
                {property.rera_registered && (
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                    <Shield className="h-3 w-3 mr-1" /> RERA: {property.rera_number || 'CGRERA100518A000114'}
                  </Badge>
                )}
              </div>

              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 leading-tight">{dispTitle}</h1>
                <p className="text-gray-500 text-sm font-mono mt-1">Code: {property.code}</p>
              </div>

              <div className="flex items-start text-gray-700 gap-2">
                <MapPin className="h-5 w-5 text-brand-orange shrink-0 mt-0.5" />
                <p>
                  {property.locality && <span>{property.locality}, </span>}
                  {property.location_label}
                  {property.city && <span>, {property.city}</span>}
                  {property.state && <span>, {property.state}</span>}
                </p>
              </div>

              {/* Price */}
              {showPrice ? (
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 rounded-2xl p-5">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{t('common.total_price', 'Total Price')}</p>
                  <p className="font-heading text-3xl font-bold bg-gradient-to-r from-brand-orange to-brand-rust bg-clip-text text-transparent">
                    {formatINR(totalPrice)}
                    {property.negotiable && <span className="text-sm text-gray-500 font-normal ml-2">· Negotiable</span>}
                  </p>
                  <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                    {showRate && (
                      <div>
                        <p className="text-xs text-gray-500">{t('common.price_per_sqft', 'Price / sq ft')}</p>
                        <p className="font-semibold text-gray-900">₹{Number(property.rate_per_sqft).toLocaleString('en-IN')}</p>
                      </div>
                    )}
                    {showGov && (
                      <div>
                        <p className="text-xs text-gray-500">{t('common.government_value', 'Govt. Value')}</p>
                        <p className="font-semibold text-gray-900">₹{Number(property.gov_rate).toLocaleString('en-IN')}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 border rounded-2xl p-5 text-center">
                  <p className="font-medium text-gray-700">{t('common.price_on_request', 'Price on Request')}</p>
                  <p className="text-xs text-gray-500 mt-1">Contact us for transparent, latest pricing</p>
                </div>
              )}

              {/* Quick stats */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {showArea && <Stat icon={Maximize2} label={t('common.area', 'Area')} value={`${Number(area).toLocaleString('en-IN')} sq ft`} />}
                {property.bhk && property.bhk !== 'NA' && <Stat icon={Home} label="Configuration" value={property.bhk} />}
                {property.plot_facing && <Stat icon={Compass} label={t('common.facing', 'Facing')} value={property.plot_facing} />}
                {property.possession_status && <Stat icon={Calendar} label={t('common.possession', 'Possession')} value={property.possession_status} />}
                {property.bathrooms != null && <Stat icon={Bath} label="Bathrooms" value={property.bathrooms} />}
                {property.covered_parking != null && <Stat icon={Car} label="Covered Parking" value={property.covered_parking} />}
                {property.floor_number != null && <Stat icon={Layers} label="Floor" value={`${property.floor_number}${property.total_floors ? ` / ${property.total_floors}` : ''}`} />}
                {property.loan_possible && <Stat icon={Banknote} label="Loan" value="Available" />}
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-2 pt-2">
                <a href={`tel:${phone.replace(/\s/g, '')}`}>
                  <Button className="bg-gradient-to-r from-brand-orange to-brand-rust text-white rounded-full" data-testid="call-now-btn">
                    <Phone className="h-4 w-4 mr-2" /> {t('cta.call_now', 'Call Now')}
                  </Button>
                </a>
                <a href={`https://wa.me/${wa}?text=${encodeURIComponent(`Hi, I'm interested in ${property.title} (${property.code})`)}`} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="rounded-full border-green-500 text-green-700 hover:bg-green-50">
                    <MessageCircle className="h-4 w-4 mr-2" /> {t('cta.whatsapp', 'WhatsApp')}
                  </Button>
                </a>
                <ShareButton
                  url={typeof window !== 'undefined' ? window.location.href : ''}
                  title={property.title}
                  description={ogDescription}
                  className="rounded-full"
                />
                <BrochureButton propertyId={property.id} propertyTitle={property.title} brochureFile={property.brochure_file} />
              </div>
            </div>
          </div>
        </section>

        {/* Description */}
        {(property.short_description || property.full_address) && (
          <section className="py-8 md:py-10 bg-gray-50/60 border-y">
            <div className="max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <h2 className="font-heading text-2xl font-bold text-gray-900">{t('common.description', 'Description')}</h2>
                {dispDesc && <p className="text-gray-700 leading-relaxed whitespace-pre-line">{dispDesc}</p>}
                {dispAddr && (
                  <div className="text-sm text-gray-600 bg-white rounded-xl border p-4">
                    <p className="font-semibold text-gray-900 mb-1">{t('common.location', 'Location')}</p>
                    <p>{dispAddr}</p>
                  </div>
                )}
                {property.pricing_note && (
                  <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 text-brand-rust">
                    {property.pricing_note}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl border p-5 space-y-3">
                <h3 className="font-heading text-lg font-semibold">Project Highlights</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  {property.rera_registered && <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />C.G. RERA Registered</li>}
                  {property.diversion_type && <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />{property.diversion_type}</li>}
                  {property.road_width_ft && <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />{property.road_width_ft} ft road width</li>}
                  {property.transaction_type && <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />{property.transaction_type} transaction</li>}
                  {property.payment_plan && <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />Payment plan: {property.payment_plan}</li>}
                  {property.loan_possible && <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" />Bank loan assistance</li>}
                </ul>
              </div>
            </div>
          </section>
        )}

        {/* Property details */}
        <section className="py-10 bg-white">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">{t('common.property_details', 'Property Details')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {property.listing_type && <DetailItem label="Listing Type" value={property.listing_type} />}
              {property.property_type && <DetailItem label="Property Type" value={property.property_type} />}
              {property.transaction_type && <DetailItem label="Transaction" value={property.transaction_type} />}
              {property.plot_category && <DetailItem label="Plot Category" value={property.plot_category} />}
              {property.commercial_type && <DetailItem label="Commercial Type" value={property.commercial_type} />}
              {property.diversion_type && <DetailItem label="Diversion" value={property.diversion_type} />}
              {showArea && <DetailItem label={t('common.area', 'Area')} value={`${Number(area).toLocaleString('en-IN')} sq ft`} />}
              {property.carpet_area_sqft && <DetailItem label="Carpet Area" value={`${Number(property.carpet_area_sqft).toLocaleString('en-IN')} sq ft`} />}
              {property.builtup_area_sqft && <DetailItem label="Built-up Area" value={`${Number(property.builtup_area_sqft).toLocaleString('en-IN')} sq ft`} />}
              {property.super_builtup_area_sqft && <DetailItem label="Super Built-up" value={`${Number(property.super_builtup_area_sqft).toLocaleString('en-IN')} sq ft`} />}
              {property.bhk && property.bhk !== 'NA' && <DetailItem label="Configuration" value={property.bhk} />}
              {property.bathrooms != null && <DetailItem label="Bathrooms" value={property.bathrooms} />}
              {property.balconies != null && <DetailItem label="Balconies" value={property.balconies} />}
              {property.floor_info && <DetailItem label="Floor Info" value={property.floor_info} />}
              {property.property_age_years != null && <DetailItem label="Age" value={`${property.property_age_years} years`} />}
              {property.construction_status && <DetailItem label="Construction" value={property.construction_status} />}
              {property.furnishing_status && <DetailItem label="Furnishing" value={property.furnishing_status} />}
              {property.flooring_type && <DetailItem label="Flooring" value={property.flooring_type} />}
              {property.water_source && <DetailItem label="Water" value={property.water_source} />}
              {property.power_backup && <DetailItem label="Power Backup" value={property.power_backup} />}
              {property.plot_facing && <DetailItem label={t('common.facing', 'Facing')} value={property.plot_facing} />}
              {property.road_width_ft && <DetailItem label="Road Width" value={`${property.road_width_ft} ft`} />}
              {property.frontage_ft && <DetailItem label="Frontage" value={`${property.frontage_ft} ft`} />}
              {property.ceiling_height_ft && <DetailItem label="Ceiling Height" value={`${property.ceiling_height_ft} ft`} />}
              {property.zone_type && <DetailItem label="Zone" value={property.zone_type} />}
              {property.signage_visibility && <DetailItem label="Signage" value={property.signage_visibility} />}
              {property.possession_status && <DetailItem label={t('common.possession', 'Possession')} value={property.possession_status} />}
              {property.available_from_date && <DetailItem label="Available From" value={property.available_from_date} />}
              {property.monthly_rent && <DetailItem label="Monthly Rent" value={formatINR(property.monthly_rent)} />}
              {property.security_deposit && <DetailItem label="Deposit" value={formatINR(property.security_deposit)} />}
              {property.lock_in_period_months && <DetailItem label="Lock-in" value={`${property.lock_in_period_months} months`} />}
              {property.lease_term_years && <DetailItem label="Lease Term" value={`${property.lease_term_years} years`} />}
              {property.maintenance_per_month && <DetailItem label="Maintenance" value={`${formatINR(property.maintenance_per_month)} / month`} />}
              {showRate && <DetailItem label={t('common.price_per_sqft', 'Rate / sq ft')} value={`₹${Number(property.rate_per_sqft).toLocaleString('en-IN')}`} />}
              {showGov && <DetailItem label={t('common.government_value', 'Govt. Value')} value={`₹${Number(property.gov_rate).toLocaleString('en-IN')}`} />}
              {showPrice && <DetailItem label={t('common.total_price', 'Total Price')} value={formatINR(totalPrice)} />}
              {property.booking_amount && <DetailItem label="Booking Amount" value={formatINR(property.booking_amount)} />}
              {property.payment_plan && <DetailItem label="Payment Plan" value={property.payment_plan} />}
              {property.owner_type && <DetailItem label="Owner Type" value={property.owner_type} />}
            </div>
          </div>
        </section>

        {/* Amenities */}
        <section className="py-10 bg-gray-50/60 border-y">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">{t('common.amenities', 'Amenities')}</h2>
            <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {AMENITY_GROUPS.map((group) => {
                const enabled = Object.entries(group.items).filter(([key]) => property[key]);
                if (!enabled.length) return null;
                return (
                  <div key={group.title.en} className="bg-white rounded-2xl border p-5">
                    <h3 className="font-heading font-semibold text-gray-900 mb-3">{lang === 'hi' ? group.title.hi : group.title.en}</h3>
                    <ul className="space-y-2 text-sm">
                      {enabled.map(([key, label]) => (
                        <li key={key} className="flex items-center gap-2 text-gray-700">
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          {lang === 'hi' ? label.hi : label.en}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Map */}
        {(mapEmbed || property.google_map_link) && (
          <section className="py-10 bg-white">
            <div className="max-w-7xl mx-auto px-4 lg:px-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-2xl font-bold text-gray-900">{t('common.location', 'Location')}</h2>
                {property.google_map_link && (
                  <a href={property.google_map_link} target="_blank" rel="noreferrer" className="text-sm text-brand-orange hover:text-brand-rust flex items-center gap-1">
                    Open in Google Maps <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
              {mapEmbed ? (
                <div className="rounded-2xl overflow-hidden border aspect-[16/9]">
                  <iframe src={mapEmbed} title="Map" className="w-full h-full" loading="lazy" />
                </div>
              ) : (
                <div className="bg-gray-50 rounded-2xl p-8 text-center text-gray-500">Map link available — click "Open in Google Maps" above</div>
              )}
            </div>
          </section>
        )}

        {/* Inquiry */}
        <section className="py-10 md:py-14 bg-gradient-to-br from-orange-50/40 to-white">
          <div className="max-w-3xl mx-auto px-4 lg:px-8">
            <div className="bg-white border rounded-2xl shadow-sm p-6 md:p-8">
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">{t('cta.enquire_now', 'Enquire Now')}</h2>
              <p className="text-gray-600 mb-5 text-sm">Fill the form and our team will reach out shortly.</p>
              <form onSubmit={submitEnquiry} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>{t('form.name', 'Name')} *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="lead-name" />
                </div>
                <div className="space-y-1">
                  <Label>{t('form.mobile', 'Mobile')} *</Label>
                  <Input type="tel" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} required data-testid="lead-mobile" />
                </div>
                <div className="space-y-1">
                  <Label>{t('form.email', 'Email')}</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label>{t('form.preferred_date', 'Preferred Date')}</Label>
                  <Input type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} />
                </div>
                <div className="md:col-span-2 space-y-1">
                  <Label>{t('form.message', 'Message')}</Label>
                  <Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                </div>
                <div className="md:col-span-2">
                  <Button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-brand-orange to-brand-rust text-white" data-testid="lead-submit-btn">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {t('cta.submit_enquiry', 'Submit Enquiry')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* Related */}
        {related.length > 0 && (
          <section className="py-10 bg-white">
            <div className="max-w-7xl mx-auto px-4 lg:px-8">
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">{t('common.related_properties', 'Related Properties')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {related.map((p) => <PropertyCard key={p.id} property={p} />)}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-white border rounded-xl p-3 flex items-start gap-3">
    <Icon className="h-5 w-5 text-brand-orange shrink-0 mt-0.5" />
    <div className="min-w-0">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="font-semibold text-gray-900 truncate">{value}</p>
    </div>
  </div>
);

const DetailItem = ({ label, value }) => (
  <div className="bg-gray-50 rounded-xl p-3">
    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</p>
    <p className="font-semibold text-gray-900 break-words">{String(value)}</p>
  </div>
);
